import { AUTO_TOC_NODE_ID, TreeDataNode, getAutoTocTitle } from '@livv/models';
import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import TreeViewSearch from '@components/TreeDrawer/TreeViewSearch';
import TreeItems from '@screens/Books/Book/components/SideBar/TreeItems';
import { useBookPageContext } from '@utils/context/book';

interface TitleTreeProps {
    expanded: string[];
    focusedPath?: string;
    hasAutoToc?: boolean;
    setExpanded: (expanded: string[]) => void;
    treeDataNodes: TreeDataNode[];
    treeId: string;
}

const TitleTree: FC<TitleTreeProps> = ({
    expanded,
    focusedPath,
    hasAutoToc = false,
    setExpanded,
    treeDataNodes,
    treeId,
}) => {
    const { bookMeta, isLegitext } = useBookPageContext();
    const visibleTreeDataNodes = useMemo(
        () => treeDataNodes.filter(({ title }) => Boolean(title.trim())),
        [treeDataNodes],
    );
    const treeDataNodesWithAutoToc = useMemo(
        () =>
            hasAutoToc
                ? [
                      {
                          id: AUTO_TOC_NODE_ID,
                          title: getAutoTocTitle(bookMeta.language),
                      },
                      ...visibleTreeDataNodes,
                  ]
                : visibleTreeDataNodes,
        [bookMeta.language, hasAutoToc, visibleTreeDataNodes],
    );
    let focusedNodeIds: string[] = [];

    if (focusedPath) {
        focusedNodeIds = focusedPath.split('/');
    } else if (hasAutoToc) {
        focusedNodeIds = [AUTO_TOC_NODE_ID];
    }
    const [searchTerm, setSearchTerm] = useState<string>('');

    useEffect(() => {
        setExpanded(focusedNodeIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusedPath]);

    return (
        <TreeViewSearch
            expanded={expanded}
            focusedNodeIds={focusedNodeIds}
            searchTerm={searchTerm}
            setExpanded={setExpanded as Dispatch<SetStateAction<string[]>>}
            setSearchTerm={setSearchTerm}
            treeDataNodes={treeDataNodesWithAutoToc}
        >
            <TreeItems
                nodes={treeDataNodesWithAutoToc}
                searchTerm={searchTerm}
                treeId={treeId}
                withLevelPrefix={!isLegitext}
            />
        </TreeViewSearch>
    );
};

export default TitleTree;
